import './style.css'

import { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { authContext } from '../../../context/auth/context';
import { roleContext } from '../../../context/role/context';
import { useTheme } from '../../../context/theme/ThemeContext';
import { useSidebar } from '../../../context/sidebar/SidebarContext';
import Swal from 'sweetalert2';

import ButtonBasic from '../../atoms/buttonBasic/view';

import {
    Avatar, Menu, MenuItem, Divider, ListItemIcon,
    Typography, Box, IconButton, ListItemText, Chip
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import CheckIcon from '@mui/icons-material/Check';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import { Menu as MenuIcon, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import pkg from '../../../../package.json';

import { getAvatarMenuItems } from './avatarMenuFactory';

function TopBar() {
    const { logOut } = useContext(authContext);
    const { profile, isInpersonated, unpersonate } = useContext(roleContext);
    const { mode, setMode } = useTheme();
    const { toggle: toggleSidebar } = useSidebar();
    const navigate = useNavigate();
    const roleId = profile?.colaborador?.role_id ?? profile?.role_id;
    const isCashier = roleId === 3;
    const isPos = location.pathname === '/pos' || isCashier;

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [updateAvailable, setUpdateAvailable] = useState(false);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const menuItems = getAvatarMenuItems({
        profile,
        isInpersonated,
        unpersonate,
        navigate,
        handleClose
    });

    const iconMap = {
        profile: <PersonIcon fontSize="small" />,
        home: <HomeIcon fontSize="small" />,
        unpersonate: <SwapHorizIcon fontSize="small" />
    };

    const handleUpdateApp = () => {
        Swal.fire({
            icon: 'info',
            title: 'Actualizando POS',
            text: 'Estamos instalando la última versión. La aplicación se reiniciará en unos segundos...',
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading();
                
                // 1. Unregister Service Workers
                if ('serviceWorker' in navigator) {
                    navigator.serviceWorker.getRegistrations().then((registrations) => {
                        for (let registration of registrations) {
                            registration.unregister();
                        }
                    }).catch(err => console.error('Error unregistering service worker:', err));
                }

                // 2. Clear cache storages
                if ('caches' in window) {
                    caches.keys().then((names) => {
                        for (let name of names) {
                            caches.delete(name);
                        }
                    }).catch(err => console.error('Error clearing caches:', err));
                }

                // 3. Clear sessionStorage state
                sessionStorage.removeItem('update_required');

                // 4. Force reload after a short delay
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        });
    };

    useEffect(() => {
        const isUpdateRequired = sessionStorage.getItem('update_required') === 'true';
        if (isUpdateRequired) {
            setUpdateAvailable(true);
        }

        const handleUpdateEvent = () => {
            setUpdateAvailable(true);
        };

        window.addEventListener('app-update-available', handleUpdateEvent);
        window.addEventListener('logout', logOut);

        return () => {
            window.removeEventListener('app-update-available', handleUpdateEvent);
            window.removeEventListener('logout', logOut);
        };
    }, [logOut]);

    const handleLogout = () => {
        handleClose();
        logOut();
    };

    const handleSetMode = (newMode) => {
        setMode(newMode);
    };

    const handleShowVersion = () => {
        handleClose();
        Swal.fire({
            title: 'Información de la Aplicación',
            html: `
                <div style="text-align: left; font-family: var(--font-family-base, sans-serif); color: var(--color-text-primary, #333); font-size: 0.95rem;">
                    <p style="margin-bottom: 8px;"><strong>Nombre:</strong> EasyPOS</p>
                    <p style="margin-bottom: 8px;"><strong>Versión:</strong> ${pkg.version || '2.3.9'}</p>
                    <p style="margin-bottom: 0;"><strong>Descripción:</strong> Sistema POS y Facturación Electrónica</p>
                </div>
            `,
            icon: 'info',
            confirmButtonText: 'Entendido',
            confirmButtonColor: 'var(--color-primary, #00aaaa)',
            customClass: {
                popup: 'premium-swal-popup'
            }
        });
    };

    const avatarUrl = profile?.url_avatar || null;
    const displayName = profile?.companyName || profile?.colaborador?.name || profile?.name || '';
    const initials = displayName.charAt(0).toUpperCase();

    return (
        <header className="topbar">
            <div className="topbar-left">
                {!isPos && (
                    <button className="topbar-menu-btn" onClick={toggleSidebar} title="Toggle sidebar (⌘B)">
                        <MenuIcon size={20} />
                    </button>
                )}
                <span
                    className="topbar-brand"
                    onClick={() => !isCashier && window.location.replace("/")}
                    style={{ cursor: isCashier ? 'default' : 'pointer' }}
                >
                    EasyPOS
                </span>
            </div>

            <div className="topbar-right">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {updateAvailable && (
                        <button onClick={handleUpdateApp} className="topbar-update-btn" title="Actualización de sistema disponible">
                            Actualizar POS
                            <ArrowRight size={14} className="topbar-update-icon" />
                        </button>
                    )}
                    <Typography
                        variant="body2"
                        sx={{ color: 'var(--color-topbar-text)', fontWeight: 500, display: { xs: 'none', sm: 'block' }, fontSize: '0.85rem' }}
                    >
                        {displayName}
                    </Typography>

                    <IconButton
                        id="topbar-avatar-btn"
                        onClick={handleOpen}
                        size="small"
                        aria-controls={open ? 'topbar-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                    >
                        <Avatar
                            src={avatarUrl}
                            sx={{ width: 32, height: 32, bgcolor: 'var(--color-secondary, #00aaaa)', fontSize: 13 }}
                        >
                            {!avatarUrl && initials}
                        </Avatar>
                    </IconButton>
                </Box>
            </div>

            <Menu
                id="topbar-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        elevation: 4,
                        sx: {
                            minWidth: 220,
                            borderRadius: 2,
                            mt: 1,
                            overflow: 'visible',
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 16,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {displayName}
                    </Typography>
                    {profile?.colaborador && (
                        <Typography variant="caption" color="text.secondary">
                            Cajero
                        </Typography>
                    )}
                </Box>

                <Divider />

                {menuItems.map(item => (
                    <MenuItem key={item.id} onClick={item.onClick}>
                        <ListItemIcon>
                            {iconMap[item.icon]}
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                    </MenuItem>
                ))}

                <Divider />

                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                        Tema
                    </Typography>
                </Box>

                <MenuItem onClick={() => handleSetMode('light')} selected={mode === 'light'}>
                    <ListItemIcon>
                        <LightModeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Claro" />
                    {mode === 'light' && <CheckIcon fontSize="inherit" sx={{ ml: 1, opacity: 0.7 }} />}
                </MenuItem>

                <MenuItem onClick={() => handleSetMode('dark')} selected={mode === 'dark'}>
                    <ListItemIcon>
                        <DarkModeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Oscuro" />
                    {mode === 'dark' && <CheckIcon fontSize="inherit" sx={{ ml: 1, opacity: 0.7 }} />}
                </MenuItem>

                <MenuItem onClick={() => handleSetMode('system')} selected={mode === 'system'}>
                    <ListItemIcon>
                        <SettingsBrightnessIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Sistema" />
                    {mode === 'system' && <CheckIcon fontSize="inherit" sx={{ ml: 1, opacity: 0.7 }} />}
                </MenuItem>

                <Divider />

                <MenuItem onClick={handleShowVersion}>
                    <ListItemIcon>
                        <InfoIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Acerca de EasyPOS" />
                </MenuItem>

                <MenuItem
                    id="topbar-logout-btn"
                    onClick={handleLogout}
                    sx={{ color: 'error.main', mt: 0.5 }}
                >
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    Cerrar Sesión
                </MenuItem>
            </Menu>
        </header>
    );
}

export default TopBar
